import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import { EmptyState, StatusChip } from "../../Components/Kit";
import { fetchReviewSettings, fetchReviews, fetchReviewStats, replyToReview, saveReviewSettings, setReviewShown } from "../../Helpers/reviewsApi";
import "../../Assets/css/notification-centre.css";
import "./reviews.css";

// Customer reviews (Notification Centre redesign, R7.6). Reviews go live on the car pages as soon as customers
// post them. Poor ones (1-2 stars) are flagged at the top and open a support ticket. Any review can be hidden
// (with a reason, kept in the history) or shown again, and answered with a reply shown under it.
const GROUPS = [
    { key: "all", label: "All" },
    { key: "poor", label: "Poor (1-2)", params: { group: "poor" } },
    { key: "three", label: "3 stars", params: { rating: 3 } },
    { key: "good", label: "Good (4-5)", params: { group: "good" } },
    { key: "hidden", label: "Hidden", params: { shown: "hidden" } },
];
const VIA = { app: "App", push: "Push", email: "Email", whatsapp: "WhatsApp", site: "Website", admin: "Admin" };
const DAY = 24 * 60 * 60 * 1000;
const starText = (n) => `${"★".repeat(n)}${"☆".repeat(5 - n)}`;
const when = (value) => (value ? new Date(value).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }) : "");
const isoDay = (date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const ReviewCard = ({ r, onChanged }) => {
    const [mode, setMode] = useState(""); // "" | "reply" | "hide"
    const [text, setText] = useState(r.reply?.text || "");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const run = async (work, done) => {
        setBusy(true);
        const result = await work();
        setBusy(false);
        if (!result.ok) return toast.error(result.message);
        toast.success(done);
        setMode("");
        return onChanged();
    };
    return (
        <article className={`rv-card ${r.poor ? "is-poor" : ""} ${r.shown ? "" : "is-hidden"}`} id={`review-${r.reviewId}`}>
            <div className="rv-card-row">
                <div className="rv-card-main">
                    <div className="rv-card-top">
                        <span className="rv-card-stars" role="img" aria-label={`${r.rating} out of 5 stars`}>{starText(r.rating)}</span>
                        {r.poor ? <StatusChip tone="bad">Poor</StatusChip> : null}
                        <StatusChip tone={r.shown ? "ok" : "neutral"}>{r.shown ? "Live on the car page" : "Hidden from the car page"}</StatusChip>
                        <span className="rv-sub">{when(r.createdAt)} · via {VIA[r.source] || "App"}</span>
                    </div>
                    {r.description ? <p className="rv-card-text">{r.description}</p> : <p className="rv-card-text rv-sub">No comment.</p>}
                    {r.tags?.length ? <div className="rv-card-tags">{r.tags.map((t) => <span key={t}>{t}</span>)}</div> : null}
                    <div className="rv-sub mt-2">
                        <strong className="text-body">{r.name}</strong>
                        {r.carName ? ` · ${r.carName}` : ""} · <Link to={`/booking/details/${r.bookingId}`}>{r.bookingId}</Link>{r.hubName ? ` · ${r.hubName}` : ""}
                    </div>
                    {r.ticketId ? <div className="rv-sub mt-1">Support ticket <Link to={`/tickets/${r.ticketId}`}>{r.ticketId}</Link> opened automatically</div> : null}
                    {!r.shown && r.hidden?.reason ? <div className="rv-sub mt-1">Hidden: "{r.hidden.reason}"</div> : null}
                    {r.reply?.text && mode !== "reply" ? <div className="rv-card-reply"><strong>Our reply:</strong> {r.reply.text}</div> : null}
                </div>
                <div className="rv-card-actions">
                    <button type="button" className="btn btn-outline-primary btn-sm" disabled={busy} onClick={() => setMode(mode === "reply" ? "" : "reply")}>{r.reply?.text ? "Edit reply" : "Reply"}</button>
                    {r.shown
                        ? <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => setMode(mode === "hide" ? "" : "hide")}>Hide</button>
                        : <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => run(() => setReviewShown(r.reviewId, true), "Shown on the car page again")}>Show on car page</button>}
                </div>
            </div>
            {mode === "reply" ? (
                <form className="rv-inline" onSubmit={(e) => { e.preventDefault(); run(() => replyToReview(r.reviewId, text.trim()), text.trim() ? "Reply saved. It shows under the review." : "Reply removed"); }}>
                    <label className="form-label small mb-1" htmlFor={`reply-${r.reviewId}`}>Reply (shown under the review on the car page)</label>
                    <textarea id={`reply-${r.reviewId}`} className="form-control form-control-sm" rows={3} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} />
                    <div className="d-flex gap-2 mt-2">
                        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? "Saving..." : "Save reply"}</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setMode("")}>Cancel</button>
                    </div>
                </form>
            ) : null}
            {mode === "hide" ? (
                <form className="rv-inline" onSubmit={(e) => { e.preventDefault(); run(() => setReviewShown(r.reviewId, false, reason.trim()), "Hidden from the car page"); }}>
                    <label className="form-label small mb-1" htmlFor={`hide-${r.reviewId}`}>Why hide it? (kept in the history)</label>
                    <input id={`hide-${r.reviewId}`} className="form-control form-control-sm" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Abusive language, names a staff member ..." />
                    <div className="d-flex gap-2 mt-2">
                        <button type="submit" className="btn btn-danger btn-sm" disabled={busy || !reason.trim()}>{busy ? "Hiding..." : "Hide review"}</button>
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setMode("")}>Cancel</button>
                    </div>
                </form>
            ) : null}
        </article>
    );
};

const Trends = ({ stats }) => {
    const max = Math.max(1, ...stats.weeks.map((w) => w.count));
    const table = (rows, label) => (
        <div className="nc-table-wrap">
            <table className="table table-sm align-middle rv-table">
                <thead><tr><th scope="col">{label}</th><th scope="col" className="text-end">Reviews</th><th scope="col" className="text-end">Average</th><th scope="col" className="text-end">Poor</th></tr></thead>
                <tbody>
                    {rows.length === 0 ? <tr><td colSpan={4} className="rv-sub">No reviews in these dates.</td></tr> : rows.slice(0, 12).map((row) => (
                        <tr key={row.id}>
                            <th scope="row" className="fw-semibold">{row.name}</th>
                            <td className="text-end">{row.count}</td>
                            <td className={`text-end ${row.average !== null && row.average < 3.5 ? "text-danger fw-semibold" : ""}`}>{row.average ?? "-"}</td>
                            <td className={`text-end ${row.poor ? "text-danger" : ""}`}>{row.poor}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
    return (
        <section className="rv-box" aria-labelledby="rv-trend-title">
            <h2 id="rv-trend-title" className="rv-h">Trends</h2>
            <div className="rv-weeks" role="img" aria-label="Reviews each week for the last 8 weeks">
                {stats.weeks.map((w) => (
                    <div key={String(w.week)} className="rv-week" title={`${w.count} reviews, average ${w.average ?? "-"}`}>
                        <span className="rv-week-avg">{w.average ?? ""}</span>
                        <span className="rv-week-bar"><span style={{ height: `${Math.round((w.count / max) * 100)}%` }} className={w.poor ? "has-poor" : ""} /></span>
                        <span className="rv-week-label">{new Date(w.week).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                    </div>
                ))}
            </div>
            <div className="rv-sub mb-3">Bars: reviews each week (from Monday); the number above is that week's average.</div>
            <div className="rv-trend-grid">
                <div><h3 className="rv-h3">By car</h3>{table(stats.cars, "Car")}</div>
                <div><h3 className="rv-h3">By hub</h3>{table(stats.hubs, "Hub")}</div>
            </div>
            <div className="rv-sub">Lowest average first. Last 90 days unless dates are chosen above.</div>
        </section>
    );
};

const SettingsBox = () => {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { (async () => { const r = await fetchReviewSettings(); if (r.ok) setS(r.data.settings); })(); }, []);
    if (!s) return null;
    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        const result = await saveReviewSettings({ requestDelayHours: Number(s.requestDelayHours), reminder: !!s.reminder, reminderAfterHours: Number(s.reminderAfterHours) });
        setBusy(false);
        if (!result.ok) return toast.error(result.message);
        setS(result.data.settings);
        return toast.success("Saved");
    };
    return (
        <section className="rv-box" aria-labelledby="rv-set-title">
            <h2 id="rv-set-title" className="rv-h">Review requests</h2>
            <p className="rv-sub">"How was your trip?" goes by push and email (and WhatsApp once a WhatsApp template is chosen for it in Notification Centre &gt; Messages), at most once per trip and never during quiet hours.</p>
            <form className="rv-settings" onSubmit={save}>
                <label>Send it <input type="number" className="form-control form-control-sm" min={0} max={72} step={0.5} value={s.requestDelayHours} onChange={(e) => setS({ ...s, requestDelayHours: e.target.value })} /> hours after the trip ends</label>
                <label className="rv-check"><input type="checkbox" checked={!!s.reminder} onChange={() => setS({ ...s, reminder: !s.reminder })} /> One reminder if there is no answer on any channel</label>
                {s.reminder ? <label>Remind <input type="number" className="form-control form-control-sm" min={1} max={168} value={s.reminderAfterHours} onChange={(e) => setS({ ...s, reminderAfterHours: e.target.value })} /> hours after the request</label> : null}
                <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? "Saving..." : "Save"}</button>
            </form>
            <div className="rv-sub mt-2">"Rate us on Google" after a 4-5 star review: {s.googleUrl ? <strong className="text-success">on</strong> : <>off until GOOGLE_REVIEW_URL is set in the server settings</>}.</div>
        </section>
    );
};

const Reviews = () => {
    const [params, setParams] = useSearchParams();
    const [group, setGroup] = useState(params.get("filter") === "poor" ? "poor" : "all");
    const [carId, setCarId] = useState("");
    const [hubId, setHubId] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [q, setQ] = useState(params.get("review") || "");
    const [search, setSearch] = useState(params.get("review") || "");
    const [page, setPage] = useState(1);
    const [list, setList] = useState(null);
    const [stats, setStats] = useState(null);
    const [month, setMonth] = useState(null);

    const filters = useMemo(() => ({
        ...(GROUPS.find((g) => g.key === group)?.params || {}),
        ...(carId ? { carId } : {}), ...(hubId ? { hubId } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}), ...(search ? { q: search } : {}),
    }), [group, carId, hubId, from, to, search]);

    const load = useCallback(async () => {
        const [l, st] = await Promise.all([fetchReviews({ ...filters, page, limit: 20 }), fetchReviewStats({ ...(from ? { from } : {}), ...(to ? { to } : {}) })]);
        if (l.ok) setList(l.data); else { toast.error(l.message); setList({ items: [], recentPoor: [], total: 0, pages: 1 }); }
        if (st.ok) setStats(st.data);
    }, [filters, page, from, to]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { (async () => { const r = await fetchReviewStats({ from: isoDay(new Date(Date.now() - 30 * DAY)) }); if (r.ok) setMonth(r.data.overall); })(); }, []);
    useEffect(() => { setPage(1); }, [filters]);

    const top = group === "all" && page === 1 && !carId && !hubId && !search ? (list?.recentPoor || []) : [];
    const topIds = new Set(top.map((r) => r.reviewId));
    const rest = (list?.items || []).filter((r) => !topIds.has(r.reviewId));

    return (
        <div className="settings-page rv-page">
            <div className="mb-3">
                <h4 className="fw-semibold mb-1">Reviews</h4>
                <p className="text-muted mb-0">Reviews go live on the car pages as soon as customers post them. Poor ones are flagged here and open a support ticket. You can hide or show any review.</p>
            </div>

            <div className="rv-stats">
                <div className="rv-stat"><div className="rv-stat-label">Average, last 30 days</div><div className="rv-stat-value">{month?.average ?? "-"} <span className="rv-star-mark" aria-hidden="true">★</span></div></div>
                <div className="rv-stat"><div className="rv-stat-label">Reviews, last 30 days</div><div className="rv-stat-value">{month?.count ?? "-"}</div></div>
                <div className="rv-stat is-bad"><div className="rv-stat-label">Poor, last 14 days</div><div className="rv-stat-value">{list ? list.recentPoor.length : "-"}</div></div>
                <div className="rv-stat"><div className="rv-stat-label">Hidden, last 90 days</div><div className="rv-stat-value">{stats?.overall?.hidden ?? "-"}</div></div>
            </div>

            <div className="rv-filters">
                <div className="nc-pills" role="group" aria-label="Stars">
                    {GROUPS.map((g) => <button key={g.key} type="button" className={`nc-pill ${group === g.key ? "is-active" : ""}`} aria-pressed={group === g.key} onClick={() => { setGroup(g.key); if (params.get("filter") || params.get("review")) setParams({}); }}>{g.label}</button>)}
                </div>
                <span className="flex-grow-1" />
                <select className="form-select form-select-sm" value={carId} onChange={(e) => setCarId(e.target.value)} aria-label="Car">
                    <option value="">All cars</option>
                    {(stats?.cars || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select className="form-select form-select-sm" value={hubId} onChange={(e) => setHubId(e.target.value)} aria-label="Hub">
                    <option value="">All hubs</option>
                    {(stats?.hubs || []).map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
                <input type="date" className="form-control form-control-sm" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From date" />
                <input type="date" className="form-control form-control-sm" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To date" />
                <form className="rv-search" onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); }}>
                    <input type="search" className="form-control form-control-sm" placeholder="Booking, customer, ticket ..." value={q} onChange={(e) => { setQ(e.target.value); if (!e.target.value) setSearch(""); }} aria-label="Search reviews" />
                </form>
            </div>

            {list === null ? <div className="rv-list"><Skeleton height={120} /><Skeleton height={120} /></div> : (
                <>
                    {top.length ? (
                        <section className="rv-list" aria-label="Poor reviews, last 14 days">
                            <h2 className="rv-h text-danger mb-0">Poor reviews, last 14 days</h2>
                            {top.map((r) => <ReviewCard key={r.reviewId} r={r} onChanged={load} />)}
                        </section>
                    ) : null}
                    <section className="rv-list" aria-label="Reviews">
                        {top.length ? <h2 className="rv-h mb-0 mt-2">All reviews</h2> : null}
                        {rest.length === 0 && !top.length ? <EmptyState title="No reviews here yet" text={list.total ? "" : "Customers are asked after each finished trip; their reviews appear here as they come in."} /> : rest.map((r) => <ReviewCard key={r.reviewId} r={r} onChanged={load} />)}
                    </section>
                    {list.pages > 1 ? (
                        <nav className="d-flex gap-2 align-items-center justify-content-center mt-3" aria-label="Pages">
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Newer</button>
                            <span className="rv-sub">Page {page} of {list.pages}</span>
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={page >= list.pages} onClick={() => setPage(page + 1)}>Older</button>
                        </nav>
                    ) : null}
                </>
            )}

            <div className="rv-bottom">
                {stats ? <Trends stats={stats} /> : <Skeleton height={240} />}
                <SettingsBox />
            </div>
        </div>
    );
};

export default Reviews;
